<template>
  <div class="privacy-policy-page">
    <div class="privacy-policy-container">
      <div class="section-header">
        <h1 class="section-title">{{ policy.title ?? 'Privacy Policy' }}</h1>
      </div>

      <div class="policy-content">
        <p>{{ policyIntro }}</p>

        <h2>1. {{ policySections.general.title }}</h2>
        <p>{{ policySections.general.content }}</p>

        <h2>2. {{ policySections.obligations.title }}</h2>
        <p>{{ policySections.obligations.content }}</p>

        <h2>3. {{ policySections.usage.title }}</h2>
        <p>{{ policySections.usage.content }}</p>
        <ul class="policy-list">
          <li v-for="item in policyUsageList" :key="item">{{ item }}</li>
        </ul>

        <h2>4. {{ policySections.retention.title }}</h2>
        <p>{{ policySections.retention.content }}</p>

        <h2>5. {{ policySections.cookies.title }}</h2>
        <p>{{ policySections.cookies.content }}</p>

        <h2>6. {{ policySections.hosting.title }}</h2>
        <p>{{ policySections.hosting.content }}</p>

        <h2>7. {{ policySections.rights.title }}</h2>
        <p>{{ policySections.rights.content }}</p>
        <ul class="policy-list">
          <li v-for="right in policyRightsList" :key="right.title">
            <strong>{{ right.title }}:</strong> {{ right.description }}
          </li>
        </ul>

        <h2>8. {{ policySections.contact.title }}</h2>
        <p>{{ policySections.contact.content }}</p>
        <p class="policy-contact-email">
          <a href="mailto:shkolabiblii@gmail.com" class="policy-link">shkolabiblii@gmail.com</a>
        </p>

        <h2>9. {{ policySections.consent.title }}</h2>
        <p>{{ policySections.consent.content }}</p>
      </div>
      
      <div class="back-button-wrap">
        <UIButton
          variant="secondary-arrow"
          arrow-direction="left"
          :href="`/${route.params.locale}`"
          :outline="false"
          
          padding="18px 34px"
          rounded="8px"
          size="large"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useLocaleSwitch } from '@/composables/locale'

definePageMeta({
  layout: 'full-width',
  ssr: false,
})

const { locale, messages } = useI18n({ useScope: 'global' })
const route = useRoute()
useLocaleSwitch()

// Read directly from messages object — bypasses vue-i18n ICU compiler.
// t() crashes on HTML content; raw messages access never compiles strings.
const policy = computed(() => {
  const msgs = (messages.value[locale.value] ?? messages.value['uk'] ?? {}) as Record<string, unknown>
  return (msgs.privacyPolicy ?? {}) as Record<string, unknown>
})

const policyIntro = computed(() => (policy.value.intro as string) ?? '')

const sections = computed(() => (policy.value.sections ?? {}) as Record<string, Record<string, string>>)

const policySections = computed(() => {
  const s = sections.value
  const sec = (key: string) => ({
    title:   (s[key]?.title ?? '') as string,
    content: (s[key]?.content ?? '') as string,
  })
  return {
    general: sec('general'), obligations: sec('obligations'), usage: sec('usage'),
    retention: sec('retention'), cookies: sec('cookies'), hosting: sec('hosting'),
    rights: sec('rights'), contact: sec('contact'), consent: sec('consent'),
  }
})

const policyUsageList = computed<string[]>(() => {
  const list = (sections.value.usage as Record<string, unknown>)?.list
  return Array.isArray(list) ? list.filter((v): v is string => typeof v === 'string') : []
})

const policyRightsList = computed<{ title: string; description: string }[]>(() => {
  const list = (sections.value.rights as Record<string, unknown>)?.list
  return Array.isArray(list)
    ? list.filter((r): r is { title: string; description: string } => typeof r === 'object' && r !== null && 'title' in r)
    : []
})

useHead({ title: (policy.value.title as string) ?? 'Privacy Policy' })
</script>

<style scoped>
.privacy-policy-page {
  min-height: 100vh;
  padding: 80px 0 100px;
  max-width: var(--max-width-container);
  margin: 0 auto;
  background: #fff;
}

.privacy-policy-container {
  max-width: var(--max-width-container);
  margin: 0 auto;
  padding: 0 40px;
}

.section-header {
  text-align: center;
  margin-bottom: 48px;
}

.section-header .section-title {
  font-family: var(--font-inter);
  font-weight: 700;
  font-size: 48px;
  color: var(--color-section-title);
  margin: 0 auto 16px;
  line-height: 1.2;
}

.policy-content {
  max-width: 800px;
  margin: 0 auto;
  line-height: 1.6;
}

.policy-content :deep(h2) {
  font-family: var(--font-inter);
  font-weight: var(--font-weight-semibold);
  font-size: 24px;
  color: var(--color-dark-blue);
  margin: 2rem 0 1rem 0;
}

.policy-content :deep(p) {
  font-family: var(--font-inter);
  font-size: var(--font-size-body);
  color: var(--color-grey);
  margin-bottom: 1rem;
}

.policy-list {
  margin: 1rem 0;
  padding-left: 1.5rem;
  font-family: var(--font-inter);
  font-size: var(--font-size-body);
  color: var(--color-grey);
}

.policy-list li {
  margin-bottom: 0.5rem;
  line-height: 1.6;
}

.policy-content :deep(.policy-email-wrap) {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
}

.policy-content :deep(.policy-email-icon) {
  flex-shrink: 0;
  vertical-align: middle;
  opacity: 0.9;
}

.policy-content :deep(.policy-link) {
  color: var(--color-teal);
  text-decoration: none;
  transition: color 0.2s ease;
}

.policy-content :deep(.policy-link:hover) {
  color: var(--color-dark-blue);
  text-decoration: underline;
}

.back-button-wrap {
  margin-top: 2rem;
  display: flex;
  justify-content: center;
}

@media (max-width: 1400px) {
  .privacy-policy-container {
    padding: 0 60px;
  }
}

@media (max-width: 991px) {
  .privacy-policy-page {
    padding: 60px 0 80px;
  }

  .privacy-policy-container {
    padding: 0 30px;
  }

  .section-header .section-title {
    font-size: 36px;
  }

  .policy-content :deep(h2) {
    font-size: 20px;
  }
}

@media (max-width: 767px) {
  .privacy-policy-container {
    padding: 0 20px;
  }

  .section-header .section-title {
    font-size: 32px;
  }

  .section-header {
    margin-bottom: 40px;
  }
}

@media (max-width: 575px) {
  .privacy-policy-page {
    padding: 40px 0 60px;
  }

  .section-header .section-title {
    font-size: 28px;
  }
  .section-title{
    margin-bottom: 25px;
  }
}
</style>